import { AxiosInstance } from 'axios'
import { Identifier, ListOptions } from './_types'
import { AttendanceNote } from './attendance-notes'
import { Disease } from './disease'
import { MedicalDocument } from './medical-document'
import { Patient } from './patient'

export enum AttendanceStatus {
  IN_PROGRESS = 'A',
  FINISHED = 'F',
  CANCELLED = 'C',
}

export type Attendance = {
  id: number
  startedAt: string
  endedAt: string | null
  heightCm: number | null
  weightKg: number | null
  healthPlan: number | null
  hypothesis: string | null
  report: string | null
  references: string | null
  note: string | null
  uploadPhotoUuid: string
  remote: boolean
  retroactiveEntry: boolean | null
  physicianId: number
  patientId: number
  diseaseId: number
  pharmaceuticalId: number
  data: Record<string, any> | null
  filledBy: string | null
  summary: Record<string, any> | null
  attendanceFormId: number | null
  guidedAttendanceFormId: number | null
  returnAppointmentAnswer: boolean | null
  returnAnswerAt: string
  returnInterval: number
  version: string | null
  status: AttendanceStatus
  lastAttendanceId: number | null

  patient: Patient
  physician: Identifier
  disease: Disease
  notes: AttendanceNote[]
  medicalDocuments: MedicalDocument[]
}

export type AttendanceAnswer = {
  id: number
  attendanceId: number
  formId: number
  questionId: number
  data: Record<string, unknown>
}

export interface GetAttendanceSummary {
  text: string
  clipboard: string
  attendanceDate: string
  attendanceId: number
}

export function attendanceEndpoints(apiClient: AxiosInstance) {
  return {
    async listByPatient(
      patientId: number,
      options: ListOptions = {},
    ): Promise<{
      items: Attendance[]
      count: number
      availableFilters?: Record<string, { text: string; value: string | number | boolean }[]>
      availableSorters?: string[]
    }> {
      return apiClient.get(`get-attendances-by-patient/${patientId}`, {
        params: {
          page: options.pagination?.page,
          itemsPerPage: options.pagination?.itemsPerPage,
          filters: options.filters ? JSON.stringify(options.filters) : undefined,
          sort: options.sort ? JSON.stringify(options.sort) : undefined,
        },
      })
    },

    async list(options: ListOptions = {}): Promise<Attendance[]> {
      return apiClient.get('list-attendances', {
        params: {
          page: options.pagination?.page,
          itemsPerPage: options.pagination?.itemsPerPage,
          filters: options.filters ? JSON.stringify(options.filters) : undefined,
          sort: options.sort ? JSON.stringify(options.sort) : undefined,
        },
      })
    },

    async listGuidedAttendanceAnswers(attendanceId: number): Promise<AttendanceAnswer[]> {
      return apiClient.get(`list-guided-attendance-form-answers/${attendanceId}`)
    },

    async saveGuidedAttendanceAnswers(body: {
      attendanceId: number
      formId: number
      answers: Array<{
        id?: number
        questionId: number
        data: Record<string, unknown>
      }>
    }) {
      return apiClient.post('save-guided-attendance-form-answers', body)
    },

    async getAttendanceForm(attendanceId: number) {
      return apiClient.get(`get-attendance-form`, {
        params: {
          attendanceId,
        },
      })
    },

    async getAttendance(attendanceId: number): Promise<Attendance> {
      return apiClient.get(`attendance/${attendanceId}`)
    },

    async patchAttendance(attendanceId: number, data: Record<string, unknown>): Promise<void> {
      return apiClient.patch(`attendance/${attendanceId}`, {
        data,
      })
    },

    async generateReport(attendanceId: number): Promise<{ data: string }> {
      return apiClient.post(`guided-attendance/report/${attendanceId}`)
    },

    async getAttendanceSummary(attendanceId: number, getCurrent: boolean = false): Promise<GetAttendanceSummary[]> {
      return apiClient.get(`get-previous-attendances`, {
        params: {
          attendanceId,
          getCurrent,
        },
      })
    },

    async generateAttendanceAiMessage(
      attendanceId: number,
      questionId: number,
      force: boolean = false,
    ): Promise<{ text: string }> {
      return apiClient.put(
        `generate-attendance-ai-message/${attendanceId}/question/${questionId}`,
        {},
        {
          params: {
            force,
          },
        },
      )
    },
  }
}
